import { inject, Service } from '@angular/core';
import { CLIENT_CONFIG } from '../dependency-injection';
import { BETTER_AUTH_CLIENT } from './better-auth';

/** contract of the module served at `${api}/auth/session-monitor.js` — kept in step by hand */
export interface SessionMonitorModule {
  subscribe(handler: () => void): () => void;
  /** resolves once the message has reached the frame, which loads after the module does */
  broadcast(): Promise<void>;
  announce(): Promise<void>;
}

@Service()
export class SessionMonitor {
  private readonly clientConfig = inject(CLIENT_CONFIG);
  private readonly authClient = inject(BETTER_AUTH_CLIENT);

  /** re-reads in flight because the monitor fired */
  private monitorRefetches = 0;

  /** a latch, not a window — better-auth's post-mutation fetch lands 10ms after the call settles */
  private pendingSelfActions = 0;

  get source() {
    if (this.monitorRefetches > 0)
      return 'external';
    if (this.pendingSelfActions > 0)
      return 'self';

    return 'unknown';
  }

  private stop: (() => void) | undefined;

  private module: SessionMonitorModule | undefined;

  /** settles when the module is usable, or when it is known not to be */
  private loaded: Promise<void> | undefined;

  /** tell the other tabs this page just changed the session — silently a no-op until the monitor has loaded */
  broadcast(): void {
    this.module?.broadcast();
  }

  /**
   * tell the other tabs that this document loaded with the session already
   * changed — the redirect back from an external sign-in
   *
   * Unlike `broadcast`, this waits on the import: `start` is fire-and-forget at
   * boot and this fires in the same breath, so the module is still in flight and
   * a call now would find nothing to call. The frame behind it does its own
   * waiting.
   */
  async announce(): Promise<void> {
    // a monitor that never loaded leaves the other tabs stale — it must not
    // also break the navigation that called this
    await this.loaded?.catch(() => void 0);
    await this.module?.announce();
  }

  /** mark that this tab is doing something expected to change the session */
  async trackSelfAction<T>(action: () => Promise<T>): Promise<T> {
    this.pendingSelfActions++;
    try {
      return await action();
    }
    finally {
      this.pendingSelfActions--;
    }
  }

  /** idempotent — the iframe is per-document, so one is enough */
  async start() {
    if (this.stop)
      return this.loaded;

    // claimed synchronously so a second start cannot inject a second iframe
    this.stop = () => void 0;

    const scriptUrl = `${this.clientConfig.api}/auth/session-monitor.js`;

    this.loaded = (async () => {
      const module = await import(/* @vite-ignore */ scriptUrl) as SessionMonitorModule;
      this.module = module;

      this.stop = module.subscribe(async () => {
        this.monitorRefetches++;
        try {
          await this.authClient.useSession.value?.refetch();
        }
        finally {
          this.monitorRefetches--;
        }
      });
    })();

    await this.loaded;
  }

  dispose(): void {
    this.stop?.();
    this.stop = undefined;
  }
}
